import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDeleteMemberMutation } from "@/libs/generated/hooks";
import type { MembersQuery } from "@/libs/generated/operations";
import { queryClient } from "@/libs/queryClient";

type MemberRecord = MembersQuery["members"]["records"][0];

type DeleteMemberDialogProps = {
  open: boolean;
  onClose: () => void;
  member: MemberRecord | null;
  onSuccess?: () => void;
};

function DeleteMemberDialog({ open, onClose, member, onSuccess }: DeleteMemberDialogProps) {
  const { t } = useTranslation();
  const deleteMember = useDeleteMemberMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
    },
  });
  const loading = deleteMember.isPending;
  const error = deleteMember.error;
  const reset = deleteMember.reset;

  const handleDelete = async () => {
    if (!member?.id) {
      toast.error(t("network.errors.400"), { position: "bottom-right" });
      return;
    }

    try {
      const result = await deleteMember.mutateAsync({
        id: member.id,
      });

      if (result?.deleteMember.success) {
        toast.success(t("network.organization.member.deleted"), { position: "bottom-right" });
        onSuccess?.();
        onClose();
      }
    } catch (_err) {
      // Error is handled by the mutation hook and displayed in the UI
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const getMemberName = () => {
    if (!member) return "";
    return `${member.assigneeFirstName} ${member.assigneeLastName}`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-member-dialog-title"
    >
      <DialogTitle id="delete-member-dialog-title">
        {t("organization.actions.delete-member.title")}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || t("network.errors.unknown")}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
            <CircularProgress size={20} />
            <span style={{ marginLeft: 8 }}>{t("common.loading")}</span>
          </Box>
        )}

        <DialogContentText>
          {t("organization.actions.delete-member.description")}
          {member && (
            <Box component="span" sx={{ fontWeight: "bold", display: "block", mt: 1 }}>
              {getMemberName()}
            </Box>
          )}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          {t("common.actions.cancel")}
        </Button>
        <Button
          onClick={handleDelete}
          disabled={loading}
          color="error"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? t("common.actions.deleting") : t("organization.actions.delete-member.text")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteMemberDialog;
